import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "context/media/MediaContext";

import { useAppDispatch, useAppSelector } from "redux/hooks";

import {
  Col,
  ElementWrapper,
  Row,
  StyledFormWrapper,
} from "components/BaseComponents/Flex";
import { Button, Panel, PanelHeader, Typography } from "components";
import { DIGIPOSTHEME } from "theme/digiPosTheme";
import { IAgentAuthListItem } from "models/Employee/employeeManagement.model";
import Alert from "components/BaseComponents/Alert/Alert";
import {
  initialsFromName,
  formatPhoneNumberView,
  maxName,
  maxUserName,
  formattingUserName,
} from "utils/textfield.utils";
import {
  StyledEmpDetailCircleWrapper,
  StyledEmpDetailIconCircle,
  StyledEmpDetailName,
  StyledEmpDetailNameTag,
  StyledEmpDetailInfoWrapper,
} from "./EmployeeDetail.styles";
import Image, { ICONS } from "components/BaseComponents/Image";

import { AuthListItem } from "../common/AuthListItem";
import AgentActiveInactive from "../common/AgentActiveInactive";
import AgentRemove from "../common/AgentRemove";
import AgentEdit from "../common/AgentEdit";
import RemoveSimBlokModal from "./RemoveSimBlokModal";
import { resetServiceError } from "redux/slices/ServiceInlineError";
import EmployeeEditModal from "../common/EmployeeEditModal";
import { resetSimModal } from "redux/slices/simBlockModalSlice";
import { FindAgent } from "helpers/FindAgent";

const EmployeeDetail: FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();
  const { name, phoneNumber, status } = useParams();
  const dispatch = useAppDispatch();

  const [isAddSuccess, setIsAddSuccess] = useState<boolean>(false);
  const [isEditChange, setIsEditChange] = useState<boolean>(true);
  const [isAddError, setIsAddError] = useState<boolean>(false);
  const [sts, setSts] = useState<string>(status!);
  const [txt, setTxt] = useState<string>(
    t("completed_successfully").toString()
  );
  const [isSimModalOpen, setIsSimModalOpen] = useState<boolean>(false);
  const [isActiveModalOpen, setIsActiveModalOpen] = useState<boolean>(false);
  const [agentAuthList, setAgentAuthList] = useState<IAgentAuthListItem[]>([]);
  const [agentProfileImage, setAgentProfileImage] = useState<string>();

  const { agents } = useAppSelector((state) => state.employeeManagement);
  const { customWarning } = useAppSelector((state) => state.serviceInlineError);
  const { isActiveAgentStatus } = useAppSelector((state) => state.simmodal);

  useEffect(() => {
    if (agents?.AgentList && agents?.AgentList?.length > 0) {
      const singleAgent = FindAgent(agents?.AgentList!, phoneNumber!);
      if (singleAgent) setAgentProfileImage(singleAgent.ProfileImage);
      if (singleAgent?.AuthList) setAgentAuthList(singleAgent.AuthList);
    }
  }, [agents, phoneNumber]);

  useEffect(() => {
    if (customWarning) {
      setIsAddSuccess(true);
      setTxt(customWarning);

      dispatch(resetServiceError());
    }
  }, [customWarning, dispatch]);

  useEffect(() => {
    if (isActiveAgentStatus) {
      setIsSimModalOpen((prev) => !prev);
      dispatch(resetSimModal());
    }
  }, [isActiveAgentStatus, dispatch, setIsSimModalOpen]);

  const simBlockRemove = () => {
    if (sts === "2") setIsActiveModalOpen((prev) => !prev);
    else setIsSimModalOpen((prev) => !prev);
  };

  return (
    <ElementWrapper
      width='872px'
      height={isMobile ? "" : "auto"}
      direction='column'
      justifyContent='flex-start'
      alignItems='flex-start'
      margin='0 auto 51px'
    >
      {(isAddSuccess || isAddError) && (
        <Alert
          alertType={isAddSuccess ? "Success" : "Error"}
          duration={9000}
          isVisible={isAddSuccess || isAddError}
          message={txt}
          setIsAddSuccess={setIsAddSuccess}
          setIsAddError={setIsAddError}
        />
      )}

      <Panel width='872px' marginTop='0px' marginBottom='0px' top='-50px'>
        <StyledEmpDetailInfoWrapper>
          {agentProfileImage && agentProfileImage !== "null" ? (
            <Image
              alt='Profile Image'
              srcSet={
                agentProfileImage
                  ? `data:image/png;base64, ${agentProfileImage}`
                  : undefined
              }
              height='130px'
              width='130px'
              borderRadius='50%'
              border={`2px solid ${DIGIPOSTHEME.colors.footerGrey}`}
              margin='10px 0 10px 10px'
              padding='3px'
            />
          ) : (
            <StyledEmpDetailNameTag>
              {maxName(formattingUserName(initialsFromName(name)))}
            </StyledEmpDetailNameTag>
          )}
        </StyledEmpDetailInfoWrapper>
        <PanelHeader />
        <StyledFormWrapper>
          <StyledEmpDetailName>
            <Typography
              width=''
              weight='700'
              size='22px'
              letterSpacing='3px'
              margin='5px 0 0 0'
            >
              {isMobile ? maxUserName(name) : name}
            </Typography>
          </StyledEmpDetailName>
          <StyledEmpDetailName>
            <Typography
              width=''
              size='16px'
              weight='500'
              color={DIGIPOSTHEME.colors.secondaryGraySecondary}
            >
              {`+90 ${formatPhoneNumberView(phoneNumber!)}`}
            </Typography>
          </StyledEmpDetailName>
          <Row
            padding='40px 0 0 0'
            backgroundColor={DIGIPOSTHEME.colors.transparent}
          >
            <Col isDisplayFlex={true} justifyContent='center'>
              <AgentEdit
                isDetail={true}
                phoneNumber={phoneNumber!}
                name={name!}
                t={t}
              />
            </Col>
            <Col isDisplayFlex={true} justifyContent='center'>
              <StyledEmpDetailCircleWrapper>
                <StyledEmpDetailIconCircle>
                  <Image
                    alt='Menu Icon'
                    height='33px'
                    srcSet={ICONS.PASSWORD_SECURITY}
                    width='33px'
                    onClick={() =>
                      navigate("/get-password", {
                        state: {
                          fromEmpDetail: true,
                          phoneNumberForSmsPass: phoneNumber,
                        },
                      })
                    }
                  />
                </StyledEmpDetailIconCircle>
                <Typography width='' size='14px'>
                  {t("password_refresh")}
                </Typography>
              </StyledEmpDetailCircleWrapper>
            </Col>
            <Col isDisplayFlex={true} justifyContent='center'>
              <StyledEmpDetailCircleWrapper>
                <StyledEmpDetailIconCircle>
                  <Image
                    alt='Menu Icon'
                    height='20px'
                    srcSet={ICONS.SIM_CARD}
                    width='20px'
                    onClick={() => simBlockRemove()}
                  />
                </StyledEmpDetailIconCircle>
                <Typography width='' size='14px'>
                  {t("unblock_sim")}
                </Typography>
              </StyledEmpDetailCircleWrapper>
            </Col>
          </Row>

          <Row
            backgroundColor={DIGIPOSTHEME.colors.whiteGrey}
            padding={isMobile ? "20px 0" : ""}
            noMargin
          >
            <Typography
              margin='20px'
              size='21px'
              color={DIGIPOSTHEME.colors.secondaryGraySecondary}
              weight='600'
            >
              {t("agent_add_auth_list")}
            </Typography>
          </Row>
          {AuthListItem({
            agentAuthList,
            isDetail: true,
            t,
          })}
        </StyledFormWrapper>

        <Row
          backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
          justifyContent='space-between'
          isResponsive
          noMargin
          gap
        >
          <Col>
            <Button
              type='button'
              width={isMobile ? "100%" : "100px"}
              text={`← ${t("back")}`}
              onClick={() =>
                navigate("/employee-panel", {
                  state: { fromAddOrEditAgent: isEditChange },
                })
              }
              color='light'
            />
          </Col>
          <Col>
            <Row
              backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
              justifyContent='flex-end'
              isResponsive
              noMargin
              gap
            >
              <AgentActiveInactive
                dispatch={dispatch}
                phoneNumber={phoneNumber!}
                t={t}
                status={status!}
                isDetail={true}
                setIsAddError={setIsAddError}
                setIsAddSuccess={setIsAddSuccess}
                setIsEditChange={setIsEditChange}
                setSts={setSts}
                setTxt={setTxt}
                sts={sts}
              />
              <AgentRemove
                t={t}
                phoneNumber={phoneNumber!}
                name={name!}
                onClick={(event: any) => event}
                isDetail={true}
              />
            </Row>
          </Col>
        </Row>
      </Panel>

      {isSimModalOpen && (
        <RemoveSimBlokModal
          acceptButtonText={t("yes").toString()}
          message={t("simcard_block_remove").toString()}
          rejectButtonText={t("no").toString()}
          setIsOpen={setIsSimModalOpen}
          title={t("information").toString()}
          phoneNumber={phoneNumber!}
        />
      )}

      {isActiveModalOpen && (
        <EmployeeEditModal
          acceptButtonText={t("yes").toString()}
          message={t("agent_cancel_edit_for_inactive_message").toString()}
          rejectButtonText={t("no").toString()}
          setIsOpen={setIsActiveModalOpen}
          title={t("information").toString()}
          phoneNumber={phoneNumber!}
          name={name!}
          isSim={true}
        />
      )}
    </ElementWrapper>
  );
};

export default EmployeeDetail;
